import { useEffect, useState } from "react";
import { useGetOne } from "react-admin";
import { isCompletedStatus, isFailedStatus } from "@/constants/documentStatus";
import { DocumentRead, DocumentStatus, ResourceName } from "@/types";
import { httpClient, getPartnerAwareApiUrl } from "@/providers/dataProvider/dataProvider";

// The async finalize (blob commit + partner association) typically lands well
// under a second after the 202, so poll tightly and give up after a generous
// ceiling rather than spinning forever if the backend never finalizes. Shared
// by the hook and the imperative waitForDocumentReady below so the two can't
// drift.
export const DOCUMENT_READY_POLL_INTERVAL = 800; // ms
export const DOCUMENT_READY_TIMEOUT = 20_000; // ms

/**
 * A document is "committed" — fetchable and safe to preview or attach — once its
 * status reaches UPLOADED (where a storage-only file lands after finalize) or a
 * completed status (COMPLETED/ARCHIVED). Other non-failed states (PROCESSING,
 * REVIEW_REQUIRED, …) are not yet ready. Exported so the hook and the imperative
 * waiter share one predicate.
 */
export const isDocumentReadyStatus = (status: DocumentStatus | undefined): boolean =>
  status === DocumentStatus.UPLOADED || isCompletedStatus(status);

interface UseDocumentReadyOptions {
  /** Begin polling only when true (e.g. once an upload has been accepted). */
  enabled?: boolean;
  pollInterval?: number;
  timeoutMs?: number;
}

interface UseDocumentReadyResult {
  /** The record is fetchable and committed — safe to request the blob. */
  ready: boolean;
  /** The committed document record, once ready. */
  document: DocumentRead | undefined;
  /** Still waiting on the backend to finalize the upload. */
  isPolling: boolean;
  /** Finalize failed (document entered a failed status) or timed out. */
  failed: boolean;
}

/**
 * Polls a freshly-uploaded document until the backend has finished its async
 * finalize (blob commit + partner association), then reports it ready to preview.
 *
 * The upload endpoint returns 202 Accepted before that work completes, so
 * requesting `GET /documents/{id}/download` immediately races the finalize and
 * 404s ("resource does not belong to user's partner"). Callers should gate the
 * download/preview on `ready` and show a brief processing state while
 * `isPolling`. Transient 404s during the race are expected and suppressed —
 * they are not surfaced to the user. (ADO #5347)
 */
export const useDocumentReady = (
  documentId: string | null | undefined,
  {
    enabled = true,
    pollInterval = DOCUMENT_READY_POLL_INTERVAL,
    timeoutMs = DOCUMENT_READY_TIMEOUT,
  }: UseDocumentReadyOptions = {}
): UseDocumentReadyResult => {
  const active = enabled && !!documentId;
  const [timedOut, setTimedOut] = useState(false);

  // Restart the timeout window whenever we begin polling a (new) document.
  useEffect(() => {
    if (!active) {
      setTimedOut(false);
      return;
    }
    setTimedOut(false);
    const timer = setTimeout(() => setTimedOut(true), timeoutMs);
    return () => clearTimeout(timer);
  }, [active, documentId, timeoutMs]);

  const { data } = useGetOne<DocumentRead>(
    ResourceName.DOCUMENTS,
    { id: documentId ?? "" },
    {
      enabled: active && !timedOut,
      // Keep polling until the record is fetchable and committed. Stop once it
      // is ready so we don't keep hitting the API after the race resolves.
      refetchInterval: (query) => {
        const status = (query.state.data as DocumentRead | undefined)?.status;
        return isDocumentReadyStatus(status) ? false : pollInterval;
      },
      // One attempt per tick — the refetchInterval drives the cadence, and a
      // 404 mid-finalize is expected rather than something to back off on.
      retry: false,
      // Swallow the expected transient 404s; they must not surface a toast.
      onError: () => {},
    }
  );

  const status = data?.status;
  const ready = active && isDocumentReadyStatus(status);
  const failed = active && (timedOut || isFailedStatus(status));

  return {
    ready,
    document: ready ? data : undefined,
    isPolling: active && !ready && !failed,
    failed,
  };
};

/** Outcome of {@link waitForDocumentReady}. */
export type DocumentReadyOutcome = "ready" | "failed" | "timeout" | "aborted";

/** A cancellable delay that resolves early when the signal aborts. */
const delay = (ms: number, signal?: AbortSignal): Promise<void> =>
  new Promise((resolve) => {
    const timer = setTimeout(resolve, ms);
    signal?.addEventListener(
      "abort",
      () => {
        clearTimeout(timer);
        resolve();
      },
      { once: true }
    );
  });

const errorStatus = (err: unknown): number | undefined =>
  typeof err === "object" && err !== null ? (err as { status?: number }).status : undefined;

/**
 * Imperative, promise-based sibling of {@link useDocumentReady} for non-hook
 * call sites — e.g. an attach that must wait for the upload's finalize to commit
 * `object_metadata` before it POSTs (#5347), which a hook can't express inside an
 * async callback. Shares this module's interval, ceiling and ready-predicate so
 * the two can't drift.
 *
 * Polls `GET /documents/{id}`:
 * - `"ready"` once the status is committed;
 * - `"failed"` immediately on a failed status — no full-ceiling hang on a doomed
 *   upload (the gap the hand-rolled copy had);
 * - `"aborted"` if `signal` fires (caller unmounted);
 * - `"timeout"` at the ceiling.
 *
 * Only a `404` is swallowed as the expected finalize race; any other fetch error
 * (403/500/network) ends the wait as `"failed"` rather than burning the ceiling
 * in silence.
 */
export async function waitForDocumentReady(
  documentId: string,
  {
    signal,
    pollInterval = DOCUMENT_READY_POLL_INTERVAL,
    timeoutMs = DOCUMENT_READY_TIMEOUT,
    readyWhen = "committed",
  }: {
    signal?: AbortSignal;
    pollInterval?: number;
    timeoutMs?: number;
    /**
     * "committed" (default): UPLOADED or a completed status — the blob is safe to
     * download or attach. "fetchable": any non-failed status — the record exists,
     * which is all opening its show page needs (!4974 thread 31130: a claims file
     * already in PROCESSING must not wait out the ceiling and land on the list).
     */
    readyWhen?: "committed" | "fetchable";
  } = {}
): Promise<DocumentReadyOutcome> {
  const deadline = Date.now() + timeoutMs;
  while (Date.now() < deadline) {
    if (signal?.aborted) return "aborted";
    try {
      const resp = await httpClient(`${getPartnerAwareApiUrl()}/documents/${documentId}`, { method: "GET", signal });
      const status = (resp.json as { status?: DocumentStatus } | undefined)?.status;
      if (isFailedStatus(status)) return "failed";
      if (readyWhen === "fetchable" || isDocumentReadyStatus(status)) return "ready";
    } catch (err) {
      if (signal?.aborted) return "aborted";
      // Expected transient 404 while the partner association finalizes (#5347) —
      // keep polling. Anything else is a real error: stop, don't burn the ceiling.
      if (errorStatus(err) !== 404) return "failed";
    }
    await delay(pollInterval, signal);
  }
  return "timeout";
}
